import type { ReactNode } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MARKUP_MAX_PERCENT, type FixedBidQuote } from '../utils/fixedBidMarkup';
import { formatDollars } from '../utils/fixedBidMarkupText';

/**
 * A fixed bid's markup (a contingency buffer for problems the contract's assumptions did not
 * foresee): the percent box and the base, markup and total it gives. The arithmetic is
 * utils/fixedBidMarkup.ts, the mirror of the server core that validates what is saved.
 */

/** The percent typed in a markup box: '' while it is empty. */
export type MarkupInput = number | '';

export function MarkupPercentField({ id, value, onChange, label = 'Markup (%)', hint }: { id: string; value: MarkupInput; onChange: (value: MarkupInput) => void; label?: string; hint?: ReactNode }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="number" min={0} max={MARKUP_MAX_PERCENT} step={0.5} value={value} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
      <p className="text-xs text-muted-foreground mt-1">{hint ?? `Added to the fixed bid as a contingency buffer for problems the assumptions do not cover; 0 to ${MARKUP_MAX_PERCENT}.`}</p>
    </div>
  );
}

/** Base, the markup line (x% = $y) and the bid total. `markupLabel` is "Contingency" where the client reads it. */
export function FixedBidQuoteLines({ quote, markupLabel = 'Markup', totalLabel = 'Bid total', testId = 'fixed-bid-quote', className = '' }: { quote: FixedBidQuote; markupLabel?: string; totalLabel?: string; testId?: string; className?: string }) {
  return (
    <dl className={`space-y-1 ${className}`} data-testid={testId}>
      <div className="flex justify-between gap-4">
        <dt className="text-muted-foreground">Base price</dt>
        <dd>{formatDollars(quote.base)}</dd>
      </div>
      <div className="flex justify-between gap-4">
        <dt className="text-muted-foreground">{markupLabel} ({quote.percent}%)</dt>
        <dd>{formatDollars(quote.markup)}</dd>
      </div>
      <div className="flex justify-between gap-4 border-t pt-1">
        <dt>{totalLabel}</dt>
        <dd>{formatDollars(quote.total)}</dd>
      </div>
    </dl>
  );
}
